import { action, makeObservable, observable } from "mobx";
import { fetchProtectedResource, isNumeric } from "../../utilities";
import { message } from "antd";
import BaseService from "../base/BaseService";

const QUOTE_REPORTS_URL = "/api/v1/admin/quote-reports";

/**
 * MobX service containing quote reports.
 */
class ReportsService extends BaseService {
	/**
	 * Reports about all quotes
	 * @type {[]}
	 */
	@observable
	reportsList = [];

	/**
	 * Is the service loading data.
	 * @type {boolean}
	 */
	@observable
	loading = false;

	/**
	 * Reports table pagination object.
	 * @type {{}}
	 */
	@observable
	pagination = {};

	/**
	 * Reports table sorting object.
	 * @type {{}}
	 */
	@observable
	sorter = {};

	/**
	 * Reports table filtering object.
	 * @type {{}}
	 */
	@observable
	filters = {};

	constructor(props) {
		super(props);
		makeObservable(this);
	}

	@action
	setReportsList = (data) => {
		this.reportsList = data;
	};

	@action
	setLoading = (loading) => {
		this.loading = loading;
		if (loading) {
			this.reportsList = [];
		}
	};

	@action
	setPagination = (pagination) => {
		this.pagination = pagination;
	};

	@action
	setSorter = (sorter) => {
		this.sorter = sorter;
	};

	@action
	removeFilter = (key) => {
		const { [key]: omit, ...rest } = this.filters;

		this.filters = rest;
	};

	@action
	changeFilter = (key, filter) => {
		this.filters[key] = filter;
	};

	/**
	 * Sends client data to the server to run business logic on it and get insurance data.
	 * @returns {Promise<void>}
	 */
	loadReportsList = async () => {
		this.setLoading(true);

		// Clear the reportsList before loading new data
		this.setReportsList([]);

		let hideLoadingMessage = message.loading("Loading...", 0);

		try {
			const pagedResults = await this.loadReports(this);
			this.setReportsList(pagedResults.content);
			this.setPagination({
				...this.pagination,
				total: pagedResults.totalElements,
			});
		} catch (error) {
			console.log(error);
			message.error(`There was an error while fetching reports data. Error: ${error}`);
		} finally {
			hideLoadingMessage();
			this.setLoading(false);
		}
	};


	/**
	 * Simply loads and returns all reports.
	 * @returns {Promise<void>}
	 */
	loadAllReports = async () => {
		this.setLoading(true);
		let hideLoadingMessage = message.loading("Loading...", 0);

		try {
			return (await this.loadReports({ filters: this.filters })).content;
		} catch (error) {
			console.log(error);
			message.error(
				`There was error while trying to fetch reports data from the server. Error: ${error}`
			);
		} finally {
			hideLoadingMessage();
			this.setLoading(false);
		}
	};

	/**
	 * Loads reports depending on the passed parameters
	 * @returns {Promise<void>}
	 */
	loadReports = async ({ sorter = {}, pagination = {}, filters = {} }) => {
		let field = sorter.field;
		const order = sorter.order;

		const currentPage = pagination.current;
		const pageSize = pagination.pageSize;

		let url = `${QUOTE_REPORTS_URL}?`;

		if (field && order) {
			if (field === "start_date") {
				field = "startDate";
			}

			if (field === "insurer") {
				field = "quote.product.insurer.name";
			}

			if (field === "product_name") {
				field = "quote.product.name";
			}

			if (field === "first_name") {
				field = "quote.organisation.firstName";
			}

			if (field === "last_name") {
				field = "quote.organisation.lastName";
			}

			if (field === "quoteId") {
				field = "quote.id";
			}

			if (field === "policy_number") {
				field = "policyNumber";
			}

			if (field === "address1") {
				field = "quote.organisation.address1";
			}

			if (field === "address2") {
				field = "quote.organisation.address2";
			}

			if (field === "gross_premium") {
				field = "quote.grossPremium";
			}

			if (field === "city") {
				field = "quote.organisation.city";
			}

			if (field === "postcode") {
				field = "quote.organisation.postcode";
			}

			url += `sort=${field},${order === "ascend" ? "asc" : "desc"}&`;
		}

		if (isNumeric(currentPage) && isNumeric(pageSize)) {
			url += `page=${currentPage - 1}&size=${pageSize}&`;
		}

		url = this.addFilterQueryString(url, filters);

		return await (await fetchProtectedResource(url)).json();
	};

	addFilterQueryString = (url, filters) => {
		let filterQueryString = "";

		Object.entries(filters).forEach(([key, filter]) => {
			if (filter.type === "betweendate") {
				/*
				filterQueryString += `${filter.field}>${filter.values[0].unix()},${
					filter.field
				}<${filter.values[1].unix()}`;
				*/
				let _filterField = filter.field;
				if (key === "start_date") {
					_filterField = "startDate";
				}
				let dateFrom = new Date(filter.values[0]);
				let dateTo = new Date(filter.values[1]);
				dateFrom.setHours(0);
				dateFrom.setMinutes(0);
				dateFrom.setSeconds(0);
				dateFrom.setMilliseconds(0);
				dateTo.setHours(23);
				dateTo.setMinutes(59);
				dateTo.setSeconds(59);
				dateTo.setMilliseconds(999);
				filterQueryString += `${_filterField}>${Math.floor(dateFrom.getTime() / 1000)},${_filterField}<${Math.floor(dateTo.getTime() / 1000)}`;

				// console.log(`Date start: ${dateFrom}\n
				// Date Temp: ${filter.values[0].unix()}
				// Date Start (UNIX): ${dateFrom.getTime()}\n
				// Date End: ${dateTo}\n
				// Date End (UNIX): ${dateTo.getTime()}`)
			}
		});

		if (filterQueryString.length > 0) {
			url += `filter=${filterQueryString}&`;
		}

		return url;
	};
}

export default ReportsService;
